import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMobile } from '../../contexts/MobileContext';
import { useAccountant } from '../../contexts/AccountantContext';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import ClientWorkspaceHeader from './ClientWorkspaceHeader';
import GlobalKeyboardShortcuts from './GlobalKeyboardShortcuts';
import WelcomeWizard from '../onboarding/WelcomeWizard';
import { CircleNotch, Warning, Clock, Crown } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { DemoModeBanner } from '../DemoModeBanner';

const API = process.env.REACT_APP_BACKEND_URL;

function SubscriptionBanner({ subscription }) {
  if (!subscription) return null;
  
  const { status, days_remaining, grace_days_remaining, in_grace_period, plan_name } = subscription;
  
  // Show banner for trial, grace period, or expired
  if (status === 'active' && !in_grace_period) return null;
  
  let message = '';
  let bgColor = '';
  let Icon = Clock;
  
  if (status === 'trial' && days_remaining <= 7) {
    message = `Your trial ends in ${days_remaining} day${days_remaining !== 1 ? 's' : ''}. Upgrade to continue using BizCore.`;
    bgColor = 'bg-amber-500/10 border-amber-500/30 text-amber-700';
    Icon = Clock;
  } else if (status === 'grace_period' || in_grace_period) {
    message = `Trial expired! You have ${grace_days_remaining || 2} day${grace_days_remaining !== 1 ? 's' : ''} grace period remaining. Upgrade now to avoid losing access.`;
    bgColor = 'bg-red-500/10 border-red-500/30 text-red-700';
    Icon = Warning;
  } else if (status === 'expired') {
    message = 'Your subscription has expired. Upgrade now to regain full access.';
    bgColor = 'bg-red-500/20 border-red-500/50 text-red-800';
    Icon = Warning;
  } else if (status === 'trial') {
    return null; // Don't show if more than 7 days remaining
  } else {
    return null;
  }
  
  return (
    <div className={cn("px-4 py-2 border-b flex items-center justify-between", bgColor)}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5" weight="bold" />
        <span className="text-sm font-medium">{message}</span>
      </div>
      <Button size="sm" variant="default" className="bg-primary hover:bg-primary/90">
        <Crown className="w-4 h-4 mr-1" />
        Upgrade Now
      </Button>
    </div>
  );
}

export default function DashboardLayout() {
  const { isAuthenticated, loading, token, user } = useAuth();
  const { isMobile, sidebarOpen, closeSidebar, sidebarCollapsed } = useMobile();
  const { isInClientContext, accountantMode } = useAccountant();
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!token || user?.is_platform_admin) return;
      try {
        const res = await axios.get(`${API}/api/platform/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscription(res.data);
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
      }
    };
    if (isAuthenticated) {
      fetchSubscription();
    }
  }, [token, isAuthenticated, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="dashboard-layout">
      {/* Demo Mode Banner */}
      <DemoModeBanner />
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeSidebar}
          data-testid="mobile-overlay"
        />
      )}
      
      <Sidebar />
      
      <div 
        className={cn(
          "transition-all duration-300 flex-1 flex flex-col",
          // Mobile -> no padding (sidebar is an overlay).
          // Desktop collapsed -> 80px (matches `w-20` on Sidebar).
          // Desktop expanded -> 256px (matches `w-64`).
          isMobile ? "pl-0" : (sidebarCollapsed ? "pl-20" : "pl-64")
        )} 
        id="main-content"
      >
        {/* Client Workspace Header - Shows when accountant is managing a client */}
        {accountantMode && isInClientContext && <ClientWorkspaceHeader />}
        
        <Topbar />
        <SubscriptionBanner subscription={subscription} />
        <main className="p-4 lg:p-6 flex-1">
          <Outlet />
        </main>
        
        {/* Footer with BizCore branding */}
        <footer className="border-t border-border bg-muted/30 py-3 px-4 lg:px-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>© {new Date().getFullYear()} BizCore™ - All rights reserved</span>
            <a href="https://bizcore.sgkapps.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-foreground transition-colors">
              <span>by</span>
              <span className="font-semibold text-[#1e3a5f]">SGK</span>
              <span className="font-semibold text-[#d4a84b]">Apps</span>
            </a>
          </div>
        </footer>
      </div>
      <GlobalKeyboardShortcuts />
      {/* First-login welcome wizard (skipped for platform admin) */}
      {!user?.is_platform_admin && <WelcomeWizard />}
    </div>
  );
}
